import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Lock, ShoppingCart, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Steps } from "@/pages/OrderVerify";
import Seo from "@/components/Seo";

export default function OrderConfirm() {
  const navigate = useNavigate();
  const [address, setAddress] = useState(null);
  const [product, setProduct] = useState(null);
  const [price, setPrice] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(true);
  const [placing, setPlacing] = useState(false);

  useEffect(() => {
    const a = sessionStorage.getItem("mtr_order_address");
    const p = sessionStorage.getItem("mtr_order_product");
    if (!a || !p) { navigate("/dashboard", { replace: true }); return; }
    const addr = JSON.parse(a);
    const prod = JSON.parse(p);
    setAddress(addr);
    setProduct(prod);
    api.post("/price", { address: addr, product_id: prod.productId })
      .then(({ data }) => setPrice(data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || err.message))
      .finally(() => setLoadingPrice(false));
  }, [navigate]);

  const placeOrder = async () => {
    setPlacing(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        address,
        product_id: product.productId,
        product_name: product.name,
        origin_url: window.location.origin,
      });
      window.location.href = data.url; // redirect to Stripe Checkout
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setPlacing(false);
    }
  };

  if (!address || !product) return null;

  return (
    <div className="container-mtr py-8 sm:py-12 max-w-2xl" data-testid="order-confirm-page">
      <Seo title="Confirm & pay — MeasureTheRoof" description="Review your order and pay securely." path="/order/confirm" noindex />
      <button onClick={() => navigate("/order/verify")} className="text-mortar hover:text-iron flex items-center gap-1.5 text-[14px] mb-4" data-testid="confirm-back">
        <ArrowLeft size={16} /> Back
      </button>

      <Steps current={2} />

      <div className="card-mtr mt-6">
        <span className="eyebrow">Step 2 · Review & pay</span>
        <h1 className="font-body font-extrabold text-navy text-[24px] mt-1 mb-5 flex items-center gap-2">
          <ShoppingCart size={22} className="text-navy" /> Order summary
        </h1>

        <dl className="divide-y divide-gravel">
          <Row label="Property"><span className="text-right">{address.full}</span></Row>
          <Row label="Report type">{product.name}</Row>
          <Row label="Coverage"><span className="text-go-green font-semibold flex items-center gap-1.5 justify-end"><ShieldCheck size={16} /> Confirmed</span></Row>
        </dl>

        <div className="mt-5 p-4 rounded-input bg-fieldstone flex items-center justify-between">
          <span className="font-bold text-navy text-[16px]">Total due today</span>
          {loadingPrice ? (
            <Loader2 className="animate-spin text-orange" size={20} />
          ) : (
            <span className="metric text-[32px]" data-testid="confirm-total-price">${Number(price?.customer_price).toFixed(2)}</span>
          )}
        </div>

        <button onClick={placeOrder} disabled={loadingPrice || placing || !price} className="btn-primary w-full mt-5" data-testid="place-order-btn">
          {placing ? <Loader2 className="animate-spin" size={18} /> : <><Lock size={16} /> Place order &amp; pay ${price ? Number(price.customer_price).toFixed(2) : "--"}</>}
        </button>
        <p className="text-[12px] text-mortar mt-3 text-center flex items-center justify-center gap-1.5">
          <Lock size={12} /> Secure payment by Stripe. You're charged once, when you place the order.
        </p>
        <p className="text-[12px] text-mortar mt-1 text-center">
          Orders cancelled before processing are refundable; completed reports are non-refundable.
        </p>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-6 py-3">
      <dt className="text-[13px] uppercase tracking-[0.06em] text-mortar font-semibold shrink-0">{label}</dt>
      <dd className="text-[15px] text-iron font-medium">{children}</dd>
    </div>
  );
}
